import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import StatusBadge from '../../components/StatusBadge';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import { Search, X, UserCheck, Trash2, CheckCircle, XCircle, Tag, Eye, EyeOff, FileText, CalendarClock } from 'lucide-react';
import InvoiceModal from '../../components/InvoiceModal';
import './AdminArticles.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Semua Status' },
  { value: 'payment_pending', label: 'Menunggu Bayar' },
  { value: 'writing', label: 'Sedang Ditulis' },
  { value: 'review_producer', label: 'Review Producer' },
  { value: 'review_reader', label: 'Review Reader' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'published', label: 'Terpublikasi' },
  { value: 'deletion_requested', label: 'Pengajuan Hapus' },
];

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editors, setEditors] = useState([]);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedEditor, setSelectedEditor] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catModal, setCatModal] = useState(null);
  const [selectedCat, setSelectedCat] = useState('');
  const [savingCat, setSavingCat] = useState(false);
  const [invoiceArticle, setInvoiceArticle] = useState(null);
  const [dateModal, setDateModal] = useState(null);
  const [dateValue, setDateValue] = useState('');
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [articlesRes, usersRes, catRes] = await Promise.all([
        API.get('/articles/admin/all'),
        API.get('/users?role=editor&limit=200'),
        API.get('/categories'),
      ]);
      setArticles(articlesRes.data.articles || articlesRes.data.data || []);
      setEditors(usersRes.data.users || usersRes.data.data || []);
      setCategories(catRes.data.categories || []);
    } catch { toast.error('Gagal memuat artikel'); }
    finally { setLoading(false); }
  };

  const handleSaveCategory = async () => {
    if (!selectedCat) { toast.error('Pilih kategori terlebih dahulu'); return; }
    try {
      setSavingCat(true);
      await API.patch(`/articles/admin/${catModal._id}/category`, { categoryId: selectedCat });
      toast.success('Kategori artikel diperbarui');
      setCatModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal memperbarui kategori'); }
    finally { setSavingCat(false); }
  };

  const handleApproveDelete = async (article) => {
    setDeleting(true);
    try {
      await API.delete(`/articles/admin/${article._id}`);
      toast.success('Artikel berhasil dihapus');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menghapus'); }
    finally { setDeleting(false); }
  };

  const handleForceDelete = async (article) => {
    setDeleting(true);
    try {
      await API.delete(`/articles/admin/${article._id}/force`);
      toast.success('Artikel berhasil dihapus');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menghapus'); }
    finally { setDeleting(false); }
  };

  const handleRejectDelete = async (article) => {
    try {
      await API.patch(`/articles/admin/${article._id}/reject-delete`);
      toast.success('Pengajuan hapus ditolak');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menolak'); }
  };

  const handleToggleVisibility = async (article) => {
    const next = !article.hidden;
    try {
      await API.patch(`/articles/admin/${article._id}/visibility`, { hidden: next });
      toast.success(next ? 'Artikel disembunyikan dari portal' : 'Artikel ditampilkan di portal');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal mengubah visibilitas'); }
  };

  // Date → nilai <input type="datetime-local"> menurut zona waktu perangkat admin
  const toLocalInput = (d) => {
    const x = d ? new Date(d) : new Date();
    x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
    return x.toISOString().slice(0, 16);
  };

  const handleSaveDate = async () => {
    if (!dateValue) { toast.error('Pilih tanggal dan jam terlebih dahulu'); return; }
    try {
      setSavingDate(true);
      await API.patch(`/articles/admin/${dateModal._id}/published-at`, { publishedAt: new Date(dateValue).toISOString() });
      toast.success('Tanggal terbit diperbarui');
      setDateModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal memperbarui tanggal terbit'); }
    finally { setSavingDate(false); }
  };

  const handleAssign = async () => {
    if (!selectedEditor) { toast.error('Pilih editor terlebih dahulu'); return; }
    try {
      setAssigning(true);
      await API.patch(`/articles/admin/${assignModal._id}/assign`, { editorId: selectedEditor });
      toast.success('Artikel berhasil ditugaskan');
      setAssignModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menugaskan'); }
    finally { setAssigning(false); }
  };

  const filtered = articles.filter(a => {
    const matchSearch = (a.title || a.what || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || a.status === statusFilter;
    const artCats = [a.category?._id, ...((a.categories || []).map(c => c._id || c))].filter(Boolean).map(String);
    const matchCategory = categoryFilter === 'all' || artCats.includes(String(categoryFilter));
    return matchSearch && matchStatus && matchCategory;
  });

  return (
    <DashboardLayout title="Kelola Artikel" subtitle="Pantau dan kelola semua artikel di platform">
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--ki-border)', borderRadius: 8, padding: '9px 12px' }}>
          <Search size={14} style={{ color: 'var(--ki-text-faint)', flexShrink: 0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari artikel..." style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--ki-text)', fontSize: 13, width: '100%' }} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="form-select" style={{ width: 'auto', padding: '9px 14px', fontSize: 13 }}>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="form-select" style={{ width: 'auto', padding: '9px 14px', fontSize: 13 }}>
          <option value="all">Semua Kategori</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(5)].map((_, i) => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ki-text-faint)', borderBottom: '1px solid var(--ki-border)' }}>
            Menampilkan {filtered.length} dari {articles.length} artikel
          </div>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead><tr><th>Judul/Topik</th><th>Kategori</th><th>Pengaju</th><th>Editor</th><th>Status</th><th>Aksi</th></tr></thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a._id}>
                  <td style={{ fontWeight: 600, fontSize: 14, maxWidth: 260 }}>{a.title || a.what}</td>
                  <td style={{ fontSize: 12, color: 'var(--ki-text-muted)' }}>{a.category?.name || '-'}</td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{a.requestedBy?.name || '-'}</td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{a.assignedEditor?.name || <span style={{ color: 'var(--ki-text-faint)', fontStyle: 'italic' }}>Belum ditugaskan</span>}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {['payment_confirmed','writing','revision_editor'].includes(a.status) && (
                        <button className="btn btn-secondary btn-sm" onClick={() => { setAssignModal(a); setSelectedEditor(a.assignedEditor?._id || ''); }}>
                          <UserCheck size={13} /> Tugaskan
                        </button>
                      )}
                      {['published','approved','review_reader','revision_reader'].includes(a.status) && (
                        <button className="btn btn-secondary btn-sm" title="Tugaskan ulang ke editor untuk diedit kembali" onClick={() => { setAssignModal(a); setSelectedEditor(a.assignedEditor?._id || ''); }}>
                          <UserCheck size={13} /> Tugaskan Ulang
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" title="Ubah kategori artikel" onClick={() => { setCatModal(a); setSelectedCat(a.category?._id || ''); }}>
                        <Tag size={13} /> Kategori
                      </button>
                      {a.status === 'published' && (
                        <button className="btn btn-secondary btn-sm" title="Ubah tanggal terbit yang tampil di berita" onClick={() => { setDateModal(a); setDateValue(toLocalInput(a.publishedAt)); }}>
                          <CalendarClock size={13} /> Tanggal
                        </button>
                      )}
                      {a.payment?.invoiceNumber && a.payment?.status === 'paid' && (
                        <button className="btn btn-secondary btn-sm" title="Lihat & unduh invoice" onClick={() => setInvoiceArticle(a)}>
                          <FileText size={13} /> Invoice
                        </button>
                      )}
                      {a.status === 'published' && (
                        <button
                          className="btn btn-sm"
                          title={a.hidden ? 'Tampilkan kembali di portal' : 'Sembunyikan dari portal'}
                          onClick={() => handleToggleVisibility(a)}
                          style={{ display: 'flex', alignItems: 'center', gap: 5,
                            background: a.hidden ? 'rgba(217,119,6,0.12)' : 'var(--in-bg-3)',
                            border: '1px solid ' + (a.hidden ? 'rgba(217,119,6,0.4)' : 'var(--ki-border)'),
                            color: a.hidden ? '#B45309' : 'var(--ki-text-muted)' }}
                        >
                          {a.hidden ? <><EyeOff size={13} /> Tersembunyi</> : <><Eye size={13} /> Tampil</>}
                        </button>
                      )}
                      {a.status === 'deletion_requested' && (
                        <>
                          <button onClick={() => setDeleteConfirm({ article: a, mode: 'approve' })} className="btn btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', color: '#DC2626' }}>
                            <CheckCircle size={12} /> Setujui Hapus
                          </button>
                          <button onClick={() => handleRejectDelete(a)} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <XCircle size={12} /> Tolak
                          </button>
                        </>
                      )}
                      <button onClick={() => setDeleteConfirm({ article: a, mode: 'force' })} className="btn btn-sm" title="Hapus paksa" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: '#DC2626', padding: '4px 8px' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {filtered.length === 0 && <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--ki-text-muted)', fontSize: 14 }}>Tidak ada artikel ditemukan.</div>}
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ki-text)' }}>
                {deleteConfirm.mode === 'force' ? 'Hapus Artikel' : 'Setujui Penghapusan'}
              </div>
              <button className="btn btn-icon btn-ghost" onClick={() => setDeleteConfirm(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--ki-text-muted)', fontSize: 14, marginBottom: 12 }}>
                {deleteConfirm.mode === 'force'
                  ? 'Artikel ini akan dihapus secara paksa tanpa persetujuan reader. Tindakan tidak dapat dibatalkan.'
                  : 'Artikel berikut akan dihapus secara permanen sesuai permintaan reader:'}
              </p>
              <div style={{ background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: 'var(--ki-text)', marginBottom: 16 }}>
                {deleteConfirm.article.title || deleteConfirm.article.what || 'Artikel tanpa judul'}
              </div>
              {deleteConfirm.mode === 'approve' && deleteConfirm.article.revisions?.slice(-1)[0]?.notes && (
                <div style={{ fontSize: 13, color: 'var(--ki-text-muted)', marginBottom: 16 }}>
                  <strong>Alasan dari reader:</strong> {deleteConfirm.article.revisions.slice(-1)[0].notes}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Batal</button>
                <button
                  onClick={() => deleteConfirm.mode === 'force' ? handleForceDelete(deleteConfirm.article) : handleApproveDelete(deleteConfirm.article)}
                  disabled={deleting}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#DC2626', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1, fontFamily: 'inherit', fontSize: 13 }}
                >
                  <Trash2 size={14} /> {deleting ? 'Menghapus...' : 'Ya, Hapus Permanen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {assignModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAssignModal(null)}>
          <div className="modal-box">
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700 }}>{assignModal.status === 'published' ? 'Tugaskan Ulang ke Editor' : 'Tugaskan ke Editor'}</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setAssignModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--ki-text-muted)', marginBottom: 16, fontSize: 14 }}>Artikel: <strong style={{ color: 'var(--ki-text)' }}>{assignModal.title || assignModal.what}</strong></p>
              {['published','approved','review_reader','revision_reader'].includes(assignModal.status) && (
                <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400E', marginBottom: 16, lineHeight: 1.5 }}>
                  Artikel ini akan dikembalikan ke status <strong>penulisan</strong> dan tidak lagi tampil di portal hingga melewati alur publikasi kembali.
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Pilih Editor</label>
                <select className="form-select" value={selectedEditor} onChange={e => setSelectedEditor(e.target.value)}>
                  <option value="">-- Pilih Editor --</option>
                  {editors.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setAssignModal(null)}>Batal</button>
                <button className="btn btn-primary" disabled={assigning} onClick={handleAssign}>{assigning ? 'Memproses...' : assignModal.status === 'published' ? 'Tugaskan Ulang' : 'Tugaskan'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {dateModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDateModal(null)}>
          <div className="modal-box">
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700 }}>Ubah Tanggal Terbit</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setDateModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--ki-text-muted)', marginBottom: 8, fontSize: 14 }}>Artikel: <strong style={{ color: 'var(--ki-text)' }}>{dateModal.title || dateModal.what}</strong></p>
              <p style={{ color: 'var(--ki-text-muted)', marginBottom: 16, fontSize: 13 }}>
                Saat ini: <strong style={{ color: 'var(--ki-text)' }}>{dateModal.publishedAt ? new Date(dateModal.publishedAt).toLocaleString('id-ID', { dateStyle: 'long', timeStyle: 'short' }) : '-'}</strong>
              </p>
              <div className="form-group">
                <label className="form-label">Tanggal &amp; Jam Terbit Baru</label>
                <input type="datetime-local" className="form-input" value={dateValue} max={toLocalInput()} onChange={e => setDateValue(e.target.value)} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--ki-text-faint)', marginBottom: 8, lineHeight: 1.5 }}>
                Tanggal ini tampil di halaman berita dan daftar artikel, serta memengaruhi urutan artikel di portal, sitemap, dan RSS. Tidak boleh di masa depan.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setDateModal(null)} disabled={savingDate}>Batal</button>
                <button className="btn btn-primary" disabled={savingDate} onClick={handleSaveDate}>{savingDate ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {catModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCatModal(null)}>
          <div className="modal-box">
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700 }}>Ubah Kategori Artikel</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setCatModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--ki-text-muted)', marginBottom: 16, fontSize: 14 }}>Artikel: <strong style={{ color: 'var(--ki-text)' }}>{catModal.title || catModal.what}</strong></p>
              <div className="form-group">
                <label className="form-label">Kategori</label>
                <select className="form-select" value={selectedCat} onChange={e => setSelectedCat(e.target.value)}>
                  <option value="">-- Pilih Kategori --</option>
                  {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setCatModal(null)}>Batal</button>
                <button className="btn btn-primary" disabled={savingCat} onClick={handleSaveCategory}>{savingCat ? 'Menyimpan...' : 'Simpan Kategori'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceModal open={!!invoiceArticle} onClose={() => setInvoiceArticle(null)} article={invoiceArticle} />
    </DashboardLayout>
  );
}
